// Password generator modal — a bottom sheet (same surface as totp-setup.js) with
// a length slider, character-class toggles, an "exclude similar" option, and a
// live strength bar. Generation runs through the pure password-gen.js core.
//
// Opened from two places:
//   - the entry form's password field — pass onUse to fill the field
//   - the sidebar — no onUse, copy-only quick path
//
// Settings (length, classes, exclude-similar — never the password itself) are
// remembered via generator-prefs.js: seeded synchronously from the session
// cache for an instant open, then hydrated from puter.kv, and saved on every
// change. Outside a Puter session this degrades to the in-memory cache.
//
// Does NOT manage auto-lock: AutoLock already bumps on click/keydown, and when
// opened from the form the form owns auto-lock for its whole lifetime. Re-arming
// it here would fire mid-edit (this mirrors totp-setup.js). Never logs the value.

import { el } from './dom.js';
import { openSheet } from './sheet.js';
import { copyWithAutoClear } from './clipboard.js';
import {
  generatePassword,
  buildPool,
  estimateEntropyBits,
  strengthLabel,
} from '../util/password-gen.js';
import { cachedOpts, loadOpts, saveOpts } from '../util/generator-prefs.js';

const STRENGTH_TEXT = {
  weak: 'Weak',
  fair: 'Fair',
  strong: 'Strong',
  'very-strong': 'Very strong',
};

const CLASSES = [
  { key: 'upper', label: 'A-Z' },
  { key: 'lower', label: 'a-z' },
  { key: 'digits', label: '0-9' },
  { key: 'symbols', label: '!@#' },
];

export function openPasswordGenerator({ onUse } = {}) {
  const opts = cachedOpts();   // session cache or defaults — instant, no async flash
  let value = '';
  let userTouched = false;     // once the user edits, don't clobber with a late kv load
  let open = true;

  const valueNode = el('div', { class: 'pw-gen-value' });
  const lengthVal = el('span', { class: 'pw-gen-length-val' }, [String(opts.length)]);
  const strengthFill = el('div', { class: 'pw-gen-strength-fill' });
  const strengthText = el('span', { class: 'pw-gen-strength-text' });

  function renderStrength() {
    const bits = estimateEntropyBits({ length: opts.length, poolSize: buildPool(opts).length });
    const level = strengthLabel(bits);
    strengthText.textContent = `${STRENGTH_TEXT[level]} · ${Math.round(bits)} bits`;
    strengthFill.style.width = `${Math.max(8, Math.min(100, Math.round((bits / 128) * 100)))}%`;
    strengthFill.dataset.level = level;
  }

  function regenerate() {
    value = generatePassword(opts);
    valueNode.textContent = value;
    renderStrength();
  }

  // A settings change: remember the user has taken over, persist, regenerate.
  function changed() {
    userTouched = true;
    saveOpts(opts);
    regenerate();
  }

  const slider = el('input', {
    type: 'range', min: '8', max: '64', step: '1', value: String(opts.length), class: 'pw-gen-slider',
    oninput: (e) => { opts.length = Number(e.target.value); lengthVal.textContent = String(opts.length); changed(); },
  });

  const toggleInputs = {};
  const toggleRow = el('div', { class: 'pw-gen-toggles' });
  for (const c of CLASSES) {
    const cb = el('input', {
      type: 'checkbox', checked: opts[c.key],
      onchange: (e) => {
        opts[c.key] = e.target.checked;
        // Never allow an empty pool: if the user just turned off the last class,
        // re-enable this one.
        if (!opts.upper && !opts.lower && !opts.digits && !opts.symbols) {
          opts[c.key] = true;
          e.target.checked = true;
        }
        changed();
      },
    });
    toggleInputs[c.key] = cb;
    toggleRow.append(el('label', { class: 'pw-gen-toggle' }, [cb, el('span', {}, [c.label])]));
  }

  const excludeCb = el('input', {
    type: 'checkbox', checked: opts.excludeSimilar,
    onchange: (e) => { opts.excludeSimilar = e.target.checked; changed(); },
  });

  // Reflect opts back onto the controls (used after the async kv hydrate).
  function syncControls() {
    slider.value = String(opts.length);
    lengthVal.textContent = String(opts.length);
    for (const c of CLASSES) toggleInputs[c.key].checked = opts[c.key];
    excludeCb.checked = opts.excludeSimilar;
  }

  const footerButtons = [
    el('button', { class: 'ghost', type: 'button', onclick: () => copyWithAutoClear(value, 'Password copied') }, ['Copy']),
  ];
  if (onUse) {
    footerButtons.push(el('button', {
      class: 'primary', type: 'button', onclick: () => { onUse(value); handle.close(); },
    }, ['Use password']));
  }

  const body = el('div', { class: 'pw-gen' }, [
    el('div', { class: 'pw-gen-output' }, [
      valueNode,
      // Regenerate is not a settings change (no saveOpts, no userTouched) — a
      // pending first-open loadOpts may still apply the saved settings.
      el('button', { class: 'ghost', type: 'button', onclick: regenerate }, ['Regenerate']),
    ]),
    el('div', { class: 'pw-gen-strength' }, [
      el('div', { class: 'pw-gen-strength-track' }, [strengthFill]),
      strengthText,
    ]),
    el('label', { class: 'pw-gen-row' }, [el('span', {}, ['Length']), slider, lengthVal]),
    el('div', { class: 'pw-gen-row' }, [el('span', {}, ['Characters']), toggleRow]),
    el('label', { class: 'pw-gen-row pw-gen-exclude' }, [excludeCb, el('span', {}, ['Exclude similar characters (I l 1 O 0)'])]),
    el('div', { class: 'form-actions' }, footerButtons),
  ]);

  const handle = openSheet({
    kind: 'bottom', title: 'Password generator', body,
    onDismiss: () => { open = false; },
  });

  regenerate();

  // Hydrate from puter.kv (cross-device / cross-reload). Skip if the sheet has
  // closed or the user already changed something before the load resolved.
  // Regenerate after applying: the first password was seeded from the pre-load
  // settings, so keeping it would be misleading. loadOpts never throws; the
  // .catch is just floating-promise hygiene.
  loadOpts().then((loaded) => {
    if (!open || userTouched) return;
    Object.assign(opts, loaded);
    syncControls();
    regenerate();
  }).catch(() => {});

  return handle;
}
